import {
  Alert,
  Checkbox,
  Modal,
  Space,
  Spin,
  Typography,
  Button,
} from "antd";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useLegalAcceptance } from "../context/LegalAcceptanceContext";
import { appMessage as message } from "../utils/appMessage";
import MarkdownRenderer from "./MarkdownRenderer";
import { wasNotified } from "../utils/notifiedError";

const { Text, Paragraph } = Typography;

const DOCUMENT_LABELS = {
  legal_notice: "legalTerms.documentTypes.legalNotice",
  disclaimer: "legalTerms.documentTypes.disclaimer",
  service: "legalTerms.documentTypes.service",
};

export default function LegalTermsModal() {
  const { t } = useTranslation();
  const {
    pendingDocuments,
    acceptanceModalOpen,
    setAcceptanceModalOpen,
    loading,
    acceptAllDocuments,
    loadError,
    user,
  } = useLegalAcceptance();

  const [agreedToAll, setAgreedToAll] = useState(false);

  const handleCancel = () => {
    if (user?.role === "sysAdmin") {
      setAcceptanceModalOpen(false);
    }
  };

  const handleAcceptAll = async () => {
    if (!agreedToAll) {
      message.warning(t("legalTerms.agreeRequired"));
      return;
    }

    try {
      await acceptAllDocuments();
      setAgreedToAll(false);
      message.success(t("legalTerms.accepted"));
    } catch (err) {
      if (!wasNotified(err)) message.error(t("legalTerms.acceptFailed"));
    }
  };

  if (loadError) {
    return (
      <Modal
        title={t("legalTerms.title")}
        open={acceptanceModalOpen}
        onCancel={handleCancel}
        footer={null}
        closable={user?.role === "sysAdmin"}
        width={700}
        centered
      >
        <Alert
          message={t("legalTerms.loadErrorTitle")}
          description={loadError}
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
        />
        <Paragraph>
          {t("legalTerms.loadErrorDescription")}
        </Paragraph>
      </Modal>
    );
  }
  return (
    <>
      <Modal
        title={t("legalTerms.title")}
        open={acceptanceModalOpen}
        onCancel={handleCancel}
        size="full"
        footer={null}
        styles={{
          body: {
            maxHeight: "80vh",
            overflowY: "auto",
          },
        }}
        closable={user?.role === "sysAdmin"}
        width={800}
        centered
      >
        <Spin spinning={loading}>
          <div style={{ paddingBottom: 16 }}>
            <Paragraph>
              {t("legalTerms.intro")}
            </Paragraph>

            {(pendingDocuments || []).map((doc) => (
              <div key={doc.id} style={{ marginBottom: 24 }}>
                <Paragraph style={{ marginBottom: 8 }}>
                  <Text strong>
                    {t(DOCUMENT_LABELS[doc.document_type] || doc.document_type)}
                  </Text>
                  <Text type="secondary"> {`v${doc.version}`}</Text>
                </Paragraph>
                <div
                  style={{
                    padding: "16px",
                    borderRadius: "4px",
                    maxHeight: "320px",
                    overflowY: "auto",
                    border: "1px solid #f0f0f0",
                  }}
                >
                  <MarkdownRenderer
                    content={doc.content || t("legalTerms.noContent")}
                  />
                </div>
              </div>
            ))}

            <Checkbox
              checked={agreedToAll}
              onChange={(e) => setAgreedToAll(e.target.checked)}
              style={{ marginTop: 24, marginBottom: 16 }}
            >
              <Text>
                {t("legalTerms.agreementCheckbox")}
              </Text>
            </Checkbox>

            <div style={{ display: "flex", justifyContent: "flex-end" }}>
              <Space>
                <Button
                  type="primary"
                  onClick={handleAcceptAll}
                  disabled={!agreedToAll || loading}
                  loading={loading}
                >
                  {t("legalTerms.acceptAndContinue")}
                </Button>
              </Space>
            </div>
          </div>
        </Spin>
      </Modal>
    </>
  );
}
